import { useEffect, useRef, useState } from "react"
import { IsolatedScanComment, IsolatedScanCommentResponse, StarAPIFetchState, UserResponse } from "../types/types"
import { addIsolatedScanThermalComment, addIsolatedScanThzComment, deleteComment, getIsolatedScanThermalComments, getIsolatedScanThzComments } from "../service/isolatedScanComment"
import { showErrorToast, showSuccessToast } from "../utils/utils"
import { getCurrentUser, getUser } from "../service/userService"

/**
 * useScanComments is responsible for fetching isolated scan comments of different types and
 * updating them.
 */
const useScanComments = () => {

  const [commentsTHz, setCommentsTHz] = useState<StarAPIFetchState<IsolatedScanComment[]>>({ status: "idle"})
  const [commentsThermal, setCommentsThermal] = useState<StarAPIFetchState<IsolatedScanComment[]>>({ status: "idle" })
  const currentUser = useRef<StarAPIFetchState<UserResponse>>({ status: "loading" })

  useEffect(() => {
    const loadUser = async () => {
      currentUser.current = { status: "loading" }
      const response = await getCurrentUser()
      
      if (response instanceof Error) {
        currentUser.current = { status: "error", error: response }
        return
      }
      currentUser.current = { status: "success", data: response as UserResponse }
    }
    loadUser()
  }, [])

  /**
   * Sets commentTHz state with comments for the given isolated scan.
   * 
   * @param isolatedScanId to load comments from
   */
  const onLoadIsolatedScanCommentsTHz = async (isolatedScanId: number) => {
    // Fetch thz comments
    setCommentsTHz({ status: "loading" })
    const response = await getIsolatedScanThzComments(isolatedScanId)
    if (response instanceof Error) {
      console.log("Error fetching comments")
      console.log(response);
      
      
      if (response.cause === "404") {
        setCommentsTHz({ status: "success", data: null })
        return
      }
      setCommentsTHz({status: "error", error: response })
      return
    }

    const comments: IsolatedScanComment[] = []

    // Fetch user for each comment
    for (let i=0; i<response.length; i++) {
      const userResponse = await getUser(response[i].user_id)
      if (response instanceof Error) {
        setCommentsTHz({ status: "error", error: response })
        return
      }
      comments.push({...response[i], user: userResponse as UserResponse})
    }

    const sortedComments = comments.sort((a, b) => parseInt(a.created_at) - parseInt(b.created_at))
    setCommentsTHz({ status: "success", data: sortedComments })
  }

  /**
   * Adds an isolated scan THz comment and reloads comments for the isolated scan.
   * 
   * @param comment to add to isolated scan THz
   * @param isolatedScanId of isolated scan to add comments to
   */
  const onAddIsolatedScanCommentTHz = async (comment: string, isolatedScanId: number) => {
    setCommentsTHz({ status: "loading" })
    const response = await addIsolatedScanThzComment(isolatedScanId, comment)
    if (response instanceof Error) {
      showErrorToast("Failed to add comment.")
      await onLoadIsolatedScanCommentsTHz(isolatedScanId)
      return
    }
    showSuccessToast("Comment added successfully.")
    await onLoadIsolatedScanCommentsTHz(isolatedScanId)
  }

  /**
   * Sets commentTHz state with comments for the given isolated scan.
   * 
   * @param isolatedScanId to load comments from
   */
  const onLoadIsolatedScanCommentsThermal = async (isolatedScanId: number) => {
    // Fetch thz comments
    setCommentsThermal({ status: "loading" })
    const response = await getIsolatedScanThermalComments(isolatedScanId)
    if (response instanceof Error) {
      if (response.cause === "404") {
        setCommentsThermal({ status: "success", data: null })
        return
      }
      setCommentsThermal({status: "error", error: response })
      return
    }

    const comments: IsolatedScanComment[] = []

    // Fetch user for each comment
    for (let i=0; i<response.length; i++) {
      const userResponse = await getUser(response[i].user_id)
      if (response instanceof Error) {
        setCommentsThermal({ status: "error", error: response })
        return
      }
      comments.push({...response[i], user: userResponse as UserResponse})
    }

    const sortedComments = comments.sort((a, b) => parseInt(a.created_at) - parseInt(b.created_at))
    setCommentsThermal({ status: "success", data: sortedComments })
  }

  /**
   * Adds an isolated scan THz comment and reloads comments for the isolated scan.
   * 
   * @param comment to add to isolated scan THz
   * @param isolatedScanId of isolated scan to add comments to
   */
  const onAddIsolatedScanCommentThermal = async (comment: string, isolatedScanId: number) => {
    setCommentsThermal({ status: "loading" })
    const response = await addIsolatedScanThermalComment(isolatedScanId, comment)
    if (response instanceof Error) {
      showErrorToast("Failed to add comment.")
      await onLoadIsolatedScanCommentsThermal(isolatedScanId)
      return
    }
    showSuccessToast("Comment added successfully.")
    await onLoadIsolatedScanCommentsThermal(isolatedScanId)
  }

  /**
   * Deletes a thz comment.
   * 
   * @param comment to delete
   * @param isolatedScanId to refresh comments
   */
  const onDeleteTHzComment = async (comment: IsolatedScanComment, isolatedScanId: number) => {
    setCommentsTHz({ status: "loading" })
    onDeleteComment(comment, () => {
      onLoadIsolatedScanCommentsTHz(isolatedScanId)
    })
  }

  /**
   * Deletes a thermal comment.
   * 
   * @param comment to delete
   * @param isolatedScanId to refresh comments
   */
  const onDeleteThermalComment = async (comment: IsolatedScanComment, isolatedScanId: number) => {
    setCommentsThermal({ status: "loading" })
    onDeleteComment(comment, () => {
      onLoadIsolatedScanCommentsThermal(isolatedScanId)
    })
  }

  /**
   * Deletes an isolated scan comment.
   * 
   * @param comment to delete
   * @param onDelete success callback function
   */
  const onDeleteComment = async (comment: IsolatedScanComment,  onDelete: () => void) => {
    if (currentUser.current.status !== "success") {
      showErrorToast("Failed to delete comment.")
      onDelete()
      return
    }

    if (currentUser.current.data === null) {
      showErrorToast("Failed to delete comment.")
      onDelete()
      return
    }

    if (comment.user.id !== currentUser.current.data?.id) {
      showErrorToast("Permission denied.")
      onDelete()
      return
    }

    const response = await deleteComment(comment.id)
    if (response instanceof Error) {
      showErrorToast("Failed to delete comment.")
      onDelete()
      return
    }
    showSuccessToast("Comment deleted.")
    onDelete()
  }


  return [{
    commentsTHz,
    commentsThermal,
    onLoadIsolatedScanCommentsTHz,
    onAddIsolatedScanCommentTHz,
    onDeleteTHzComment,
    onLoadIsolatedScanCommentsThermal,
    onAddIsolatedScanCommentThermal,
    onDeleteThermalComment
  }]
}

export default useScanComments